import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useContext, useState } from 'react';
import MlxActionIcon from '../../../components/common/MlxActionIcon/MlxActionIcon';
import MlxInputSwitch from '../../../components/common/MlxInputSwitch/MlxInputSwitch';
import MlxBlockUI from '../../../components/common/MlxBlockUI/MlxBlockUI';
import {
    _SubscriptionPlanFromAPI,
    getAllSubscriptionPlans,
    deleteSubscriptionPlan,
    updateSubscriptionPlan,
} from '../../../helpers/subscriptionHelper';
import { confirmPopup } from 'primereact/confirmpopup';
import { AuthCtx } from '../../../context/AuthCtxProvider';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { checkActionSubPermission } from '../../../utils/common';
import { PermissionKeys, SubPermissionKeys, AlertVariant } from '../../../constants/constants';
import { SUBSCRIPTION_PLANS_LIST } from '../../../constants/queryKeys';
import MlxToolTip from '../../../components/common/MlxToolTip/MlxToolTip';
import MlxConfirmPopup from '../../../components/common/MlxConfirmPopup/MlxConfirmPopup';
import { _ToastMessageProps } from '../../../constants/staticTypes';
import logger from '../../../utils/logger';
import './SubscriptionPlanList.scss';

type Props = {
    onEditPlan?: (plan: _SubscriptionPlanFromAPI) => void;
    setToastMessage: React.Dispatch<React.SetStateAction<_ToastMessageProps>>;
};

function SubscriptionPlanList({ onEditPlan, setToastMessage }: Props) {
    const [subscriptionPlans, setSubscriptionPlans] = useState<_SubscriptionPlanFromAPI[]>([]);
    const { userType } = useContext(AuthCtx);
    const queryClient = useQueryClient();

    // Fetch subscription plans from API
    const subscriptionPlansQuery = useQuery({
        queryKey: [SUBSCRIPTION_PLANS_LIST],
        queryFn: () => getAllSubscriptionPlans(queryClient),
        onSuccess: (response) => {
            const plansData = response?.data?.data as _SubscriptionPlanFromAPI[];
            setSubscriptionPlans(plansData || []);
        },
        onError: (error) => logger.log('Failed to fetch subscription plans', error),
        refetchOnWindowFocus: false,
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: (planId: string) => deleteSubscriptionPlan(queryClient, planId),
        onSuccess: (response: any) => {
            queryClient.invalidateQueries([SUBSCRIPTION_PLANS_LIST]);
            setToastMessage({
                show: true,
                message: response?.message || 'Subscription plan deleted successfully',
                variant: AlertVariant.SUCCESS,
            });
        },
        onError: (error: any) => {
            logger.log('Failed to delete subscription plan', error);
            setToastMessage({
                show: true,
                message: error?.response?.data?.message || 'Failed to delete subscription plan. Please try again.',
                variant: AlertVariant.ERROR,
            });
        },
    });

    // Status update mutation
    const statusUpdateMutation = useMutation({
        mutationFn: (data: { id: string; isActive: boolean }) => updateSubscriptionPlan(queryClient, data),
        onSuccess: (response: any) => {
            queryClient.invalidateQueries([SUBSCRIPTION_PLANS_LIST]);
            setToastMessage({
                show: true,
                message: response?.message || 'Subscription plan status updated successfully',
                variant: AlertVariant.SUCCESS,
            });
        },
        onError: (error: any) => {
            logger.log('Failed to update subscription plan status', error);
            setToastMessage({
                show: true,
                message: error?.response?.data?.message || 'Failed to update subscription plan status. Please try again.',
                variant: AlertVariant.ERROR,
            });
        },
    });

    const handleStatusChange = (rowData: _SubscriptionPlanFromAPI) => {
        statusUpdateMutation.mutate({
            id: rowData.id,
            isActive: !rowData.isActive,
        });
    };

    const confirmStatusChange = (event: any, rowData: _SubscriptionPlanFromAPI) => {
        confirmPopup({
            target: event.target,
            message: `Are you sure you want to ${rowData.isActive ? 'deactivate' : 'activate'} this subscription plan?`,
            icon: 'pi pi-info-circle',
            defaultFocus: 'accept',
            accept: () => handleStatusChange(rowData),
            reject: () => {},
        });
    };

    const getPlanIcon = (planName: string) => {
        const iconMap: Record<string, { icon: string; backgroundColor: string; color: string }> = {
            'Free Plan': { icon: 'ri-gift-line', backgroundColor: '#E8E8E8', color: '#616161' },
            Starter: { icon: 'ri-gift-line', backgroundColor: '#E8E8E8', color: '#616161' },
            Bronze: { icon: 'ri-star-line', backgroundColor: '#FFF4E6', color: '#F57C00' },
            'Bronze Plan': { icon: 'ri-star-line', backgroundColor: '#FFF4E6', color: '#F57C00' },
            Silver: { icon: 'ri-flashlight-line', backgroundColor: '#E8F4FD', color: '#0288D1' },
            'Silver Plan': { icon: 'ri-flashlight-line', backgroundColor: '#E8F4FD', color: '#0288D1' },
            Gold: { icon: 'ri-vip-crown-line', backgroundColor: '#FFF9E6', color: '#F9A825' },
            'Gold Plan': { icon: 'ri-vip-crown-line', backgroundColor: '#FFF9E6', color: '#F9A825' },
            Growth: { icon: 'ri-plant-line', backgroundColor: '#F0E6FF', color: '#7B1FA2' },
        };
        return iconMap[planName] || { icon: 'ri-gift-line', backgroundColor: '#E8E8E8', color: '#616161' };
    };

    const planNameTemplate = (rowData: _SubscriptionPlanFromAPI) => {
        const iconData = getPlanIcon(rowData.name);
        const iconClass = rowData.metadata?.planIconClass || rowData.additionalData?.metadata?.planIconClass || iconData.icon;

        return (
            <div className="d-flex align-items-center">
                <span
                    className="d-flex align-items-center justify-content-center me-2"
                    style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: iconData.backgroundColor,
                        color: iconData.color,
                        fontSize: '1rem',
                    }}
                >
                    <i className={iconClass}></i>
                </span>
                <span className="me-2">{rowData.name}</span>
            </div>
        );
    };

    const pricingTemplate = (rowData: _SubscriptionPlanFromAPI) => {
        if (!rowData.prices || rowData.prices.length === 0) return 'N/A';
        const price = rowData.prices[0];
        const amount = parseFloat(price.amount);
        const interval = price.recurring?.interval || 'N/A';
        return amount === 0 ? 'Free' : `$${amount.toFixed(2)}/${interval}`;
    };

    const descriptionTemplate = (rowData: _SubscriptionPlanFromAPI) => {
        return (
            <MlxToolTip target={`.description-${rowData.id}`}>
                <div
                    className={`description-${rowData.id} overflow-ellipsis`}
                    data-pr-tooltip={rowData?.description}
                    data-pr-position="bottom"
                >
                    {rowData?.description || 'N/A'}
                </div>
            </MlxToolTip>
        );
    };

    const statusTemplate = (rowData: _SubscriptionPlanFromAPI) => {
        const handleStatusClick = (event: any) => {
            confirmStatusChange(event, rowData);
        };

        return (
            <div className="d-flex flex-row align-items-center justify-content-between" style={{ maxWidth: '110px', minWidth: '110px' }}>
                <span className="me-2">{rowData.isActive ? 'Active' : 'Inactive'}</span>
                <MlxInputSwitch name="subscription-plan-status" checked={rowData.isActive} onClick={handleStatusClick} />
            </div>
        );
    };

    const handleEdit = (rowData: _SubscriptionPlanFromAPI) => {
        if (onEditPlan) {
            onEditPlan(rowData);
        }
    };

    const handleDelete = (rowData: _SubscriptionPlanFromAPI) => {
        deleteMutation.mutate(rowData.id);
    };

    const confirmDelete = (event: any, rowData: _SubscriptionPlanFromAPI) => {
        confirmPopup({
            target: event.currentTarget,
            message: 'Are you sure you want to delete this subscription plan?',
            icon: 'pi pi-info-circle',
            defaultFocus: 'accept',
            accept: () => handleDelete(rowData),
            reject: () => {},
        });
    };

    const actionTemplate = (rowData: _SubscriptionPlanFromAPI) => {
        return (
            <div className="d-flex flex-row align-items-center justify-content-end" style={{ minWidth: '70px' }}>
                {userType === 'superAdmin' ||
                checkActionSubPermission(queryClient, PermissionKeys.MANAGE_STAFF, SubPermissionKeys.EDIT_STAFF) ? (
                    <MlxActionIcon className="me-3 edit-icon-selection" actionType="EDIT" onClick={() => handleEdit(rowData)} />
                ) : (
                    <></>
                )}
                {userType === 'superAdmin' ||
                checkActionSubPermission(queryClient, PermissionKeys.MANAGE_STAFF, SubPermissionKeys.DELETE_STAFF) ? (
                    <MlxActionIcon
                        className="me-3 delete-icon-selection"
                        actionType="DELETE"
                        onClick={(event) => confirmDelete(event, rowData)}
                    />
                ) : (
                    <></>
                )}
            </div>
        );
    };

    return (
        <>
            <MlxConfirmPopup />
            <MlxBlockUI isLoading={subscriptionPlansQuery.isFetching} opacity={0.5}>
                <DataTable
                    className="custom-p-table"
                    value={subscriptionPlans}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    size="small"
                    removableSort
                    scrollable
                    scrollHeight="flex"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
                    dataKey="id"
                >
                    <Column
                        style={{ maxWidth: '12rem', minWidth: '12rem' }}
                        header="Plan Name"
                        field="name"
                        sortable
                        body={planNameTemplate}
                    ></Column>
                    <Column style={{ maxWidth: '12rem', minWidth: '12rem' }} field="prices" header="Price" body={pricingTemplate}></Column>
                    <Column
                        style={{ maxWidth: '18rem', minWidth: '18rem' }}
                        field="description"
                        header="Description"
                        body={descriptionTemplate}
                    ></Column>
                    <Column style={{ minWidth: '110px' }} header="Status" body={statusTemplate}></Column>
                    <Column header="" style={{ minWidth: '70px' }} body={actionTemplate}></Column>
                </DataTable>
            </MlxBlockUI>
        </>
    );
}

export default SubscriptionPlanList;
